import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { MapPin, FileText, ShieldCheck } from "lucide-react";
import api from "../api";
import { useApp } from "../context/AppContext";
import { Seo } from "../components/Seo";
import { Button } from "../components/ui/button";

export default function DistrictPage({ slugOverride }) {
  const params = useParams();
  const slug = slugOverride || params.slug;
  const { t } = useApp();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [services, setServices] = useState([]);

  useEffect(() => {
    api.get(`/district-info/${slug}`).then((r) => setData(r.data)).catch(() => {});
    api.get("/services").then((r) => setServices(r.data.slice(0, 8))).catch(() => {});
  }, [slug]);

  if (!data) return <div className="max-w-5xl mx-auto px-4 py-16 text-center text-muted-foreground">Loading...</div>;
  const d = data.district;

  return (
    <div className="max-w-6xl mx-auto px-4 py-10" data-testid="district-page">
      <Seo title={`${d.name} Property Documents & Verification Services`} description={`Get 7/12, 8-A, Index-2, Certified Copy, Property Card and verification services in ${d.name}, Gujarat.`} />
      <script type="application/ld+json">{JSON.stringify({
        "@context": "https://schema.org", "@type": "Service",
        name: `${d.name} Property Documents & Verification Services`,
        provider: { "@type": "Organization", name: "DocGalaxy Gujarat Property Services" },
        areaServed: d.name + ", Gujarat",
      })}</script>
      <span className="inline-flex items-center gap-1 text-xs font-bold text-secondary bg-secondary/10 px-3 py-1 rounded-full"><MapPin size={12} /> Gujarat</span>
      <h1 className="font-display font-bold text-3xl md:text-5xl mt-3" data-testid="district-headline">
        {d.name} Property Documents & Verification Services
      </h1>
      <p className="text-muted-foreground mt-3 max-w-2xl text-base md:text-lg">
        Request {d.name} 7/12, 8-A, Index-2, Certified Copy, Property Card, Ferfar records and property verification — online, tracked, delivered.
      </p>
      <div className="mt-5 flex gap-3 flex-wrap">
        <Button className="bg-primary h-11 px-6" onClick={() => navigate("/services")} data-testid="district-find-doc-btn">{t("find_document")}</Button>
        <Button variant="outline" className="h-11 px-6" onClick={() => navigate("/verification")} data-testid="district-verify-btn">{t("verify_property")}</Button>
      </div>

      {data.sros?.length > 0 && (
        <section className="mt-10" data-testid="district-sros">
          <h2 className="font-display font-bold text-xl mb-3">{d.name} SRO Information</h2>
          <div className="grid sm:grid-cols-2 gap-3">
            {data.sros.map((s) => (
              <div key={s.id} className="bg-white border border-border rounded-xl p-4">
                <p className="font-semibold text-sm">{s.name}</p>
                <p className="text-xs text-muted-foreground mt-1">{s.address}</p>
              </div>
            ))}
          </div>
        </section>
      )}

      <section className="mt-10" data-testid="district-services">
        <h2 className="font-display font-bold text-xl mb-3">Popular services in {d.name}</h2>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
          {services.map((s) => (
            <div key={s.id} className="bg-white border border-border rounded-xl p-4 card-hover">
              <FileText size={18} className="text-primary" />
              <p className="font-semibold text-sm mt-2">{d.name} {s.name_en}</p>
              <p className="text-xs text-muted-foreground">₹{s.price} · {s.eta}</p>
              <Button size="sm" className="mt-3 w-full bg-secondary" onClick={() => navigate(`/order/new/${s.slug}`)} data-testid={`district-request-${s.slug}`}>{t("request_now")}</Button>
            </div>
          ))}
        </div>
      </section>

      <section className="mt-10 bg-muted rounded-xl p-6 flex items-center justify-between flex-wrap gap-4" data-testid="district-cta">
        <div className="flex items-center gap-3">
          <ShieldCheck className="text-secondary" size={28} />
          <p className="font-semibold">Need help with {d.name} property documents?</p>
        </div>
        <Button className="bg-primary" asChild data-testid="district-contact-btn"><Link to="/contact">{t("contact")}</Link></Button>
      </section>
    </div>
  );
}
